import type { Dot, Stick } from '@tools/math/Verlet';
import type { LifeCycleEntity } from '@tools/LifeCycle';
import { Modifier } from './Modifier';
import { Controls } from './Controls';

export interface ScrollOptions {
	auto: number;
	pauseOnHover: boolean;
	mode: 'scroll' | 'interval';
}

export class AutoScroll extends Modifier implements LifeCycleEntity {
	private readonly options: ScrollOptions;
	private paused = false;

	private mainDot!: Dot;
	private mainStick!: Stick;
	private lastTime: number = 0;
	private deltaTime: number = 0;

	constructor(options: Partial<ScrollOptions> = {}) {
		super();

		this.lifeCycle.register(this);

		this.options = {
			auto: -1.5,
			pauseOnHover: true,
			mode: 'scroll',
			...options
		};
	}

	public async load() {
		const connection = this.createConnection();
		this.mainDot = connection.dot;
		this.mainDot.pinned = true;
		this.mainStick = connection.stick;
	}

	public async awake() {
		this.connection.containerElement?.addEventListener('mouseenter', this.onEnter);
		this.connection.containerElement?.addEventListener('mouseleave', this.onLeave);
	}

	public async sleep() {
		this.connection.containerElement?.removeEventListener('mouseenter', this.onEnter);
		this.connection.containerElement?.removeEventListener('mouseleave', this.onLeave);
	}

	public async start() {
		this.lastTime = performance.now();
	}

	public async stop() {
		this.strength = 0;
		this.mainStick.stiffness = 0;
	}

	private onEnter = () => {
		if (this.options.pauseOnHover) {
			this.paused = true;
		}
	};

	private onLeave = () => {
		if (this.options.pauseOnHover) {
			this.paused = false;
		}
	};

	public tick(): void {
		if (this.paused) {
			this.mainStick.stiffness = 0;
			this.strength = 0;
			this.deltaTime = 0;
			return;
		}

		if (this.options.mode === 'scroll') {
			this.strength = Math.min(1, this.strength + 0.005);
			this.mainStick.stiffness = 0.1 * this.strength;
			this.mainDot.pos.x += this.options.auto;

			// console.log(this.mainDot.pos.x);
		} else if (this.options.mode === 'interval') {
			this.deltaTime += performance.now() - this.lastTime;
			this.lastTime = performance.now();
			if (this.deltaTime > Math.abs(this.options.auto * 1000)) {
				if (this.options.auto > 0) {
					this.getModifier(Controls)?.gotoPrev();
				} else {
					this.getModifier(Controls)?.gotoNext();
				}
				this.deltaTime = 0;
			}
		}
	}
}
